import React, {useCallback, useRef, forwardRef, useImperativeHandle, MouseEvent} from 'react';
import {getSrcFromU8Array} from '@priority-software/priority-utils/lib/file-processing';
import {Icon, PriorityIcon, Text} from '@priority-software/priority-style-react';
import {sendMonitor} from '../../utils/monitoring';
import {AttachmentType} from './index';
import cx from 'classnames';
import {createDataTestId} from '../../utils/dataTestId';
import styles from './styles.module.scss';

type Props = {
    readOnly: boolean;
    file: AttachmentType;
    monitor?: {send: boolean; text: string};
    setFile: (file: File[]) => Promise<void>;
    placeholder?: string;
    accept?: string;
    componentClasses?: string;
    icon?: PriorityIcon;
    multiple?: boolean;
    defaultMargin?: boolean; // this is hack of EP-38877 issue. I want update this logic in the future
    disabled?: boolean;
    uploaderComponentClasses?: string;
};

export const Uploader = forwardRef((props: Props, ref) => {
    const {
        readOnly,
        file,
        monitor,
        placeholder,
        setFile,
        accept,
        componentClasses,
        multiple,
        defaultMargin = true,
        disabled,
        uploaderComponentClasses
    } = props;
    const inputRef = useRef<HTMLInputElement>(null);

    const openFileExplorer = useCallback((e: MouseEvent<HTMLInputElement>) => {
        e.stopPropagation();
        if (inputRef.current) {
            inputRef.current.value = null;
            inputRef.current.click();
        }
    }, []);

    useImperativeHandle(
        ref,
        () => ({
            openFileExplorer
        }),
        [openFileExplorer]
    );

    const onInputFileChange = useCallback(
        async e => {
            e.preventDefault();
            if (!disabled) {
                await setFile(e.target.files);
            }
        },
        [setFile, disabled]
    );

    const monitorInputClick = useCallback(() => {
        if (!file?.Data && monitor?.send) {
            sendMonitor(monitor.text);
        }
    }, [monitor, file]);

    if (readOnly && !file?.Data) {
        return null;
    } else if (file?.Data?.length && file.MimeType) {
        const src = getSrcFromU8Array(file.Data, file.MimeType);

        return (
            <div className={cx(styles.previewWrap, 'pui-fileUploader-preview')}>
                <img className={styles.previewImg} src={src} alt="Image preview" />
            </div>
        );
    } else {
        return (
            <div
                className={cx(styles.fileUploaderContainer, {
                    [styles.defaultMargin]: defaultMargin,
                    [styles.disabled]: disabled,
                    [uploaderComponentClasses]: uploaderComponentClasses
                })}
            >
                <div
                    className={cx(styles.dropzone, componentClasses)}
                    onClick={openFileExplorer}
                    {...createDataTestId(`uploader`, false)}
                >
                    <input
                        ref={inputRef}
                        className={styles.input}
                        type="file"
                        multiple={multiple}
                        accept={accept}
                        onChange={onInputFileChange}
                        onClick={monitorInputClick}
                        disabled={disabled}
                    />

                    <Text tag="div" skin="paragraph-2" componentClasses={styles.text}>
                        {placeholder}
                    </Text>

                    <Icon icon={props.icon} />
                </div>
            </div>
        );
    }
});
